import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { Destination, DestinationsStorageApiClient } from '../services/DestinationsApiClient';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { promisefy } from '../utils/promisefy';
import { AnyString } from '../types/util';
import { OutputId } from '../types/obs';

export const streamDestinationsQueryOptions = (
  ...[props]: ConstructorParameters<typeof DestinationsStorageApiClient>
) =>
  queryOptions({
    queryKey: ['streamDestinations'],
    queryFn: async () => {
      const result = await new DestinationsStorageApiClient(props).getDestinations();
      return new Map(result.map((destination) => [destination.id, destination]));
    },
    initialData: new Map<string, Destination>(),
    placeholderData: keepPreviousData,
  });

export const streamDestinationStorageQuery = ({
  id,
  ...props
}: {
  id: 'default' | AnyString;
} & ConstructorParameters<typeof DestinationsStorageApiClient>[0]) =>
  queryOptions({
    queryKey: ['streamDestinations', 'destination', id],
    queryFn: async () => {
      await ensureOBSPresence();

      const storage = await new DestinationsStorageApiClient(props).getDestinationById({ id });
      const obsRegisteredOutputs = await promisefy(window.host.getAllStreamingOutputs)();

      const ownedRegisteredOutputs = storage?.id
        ? Object.values(obsRegisteredOutputs).filter((output) => {
            const { auxiliaryData } = output as Destination['outputs'][number];
            return auxiliaryData?.destinationId === storage.id;
          })
        : [];

      return {
        storage,
        registeredOutputs: (id === 'default'
          ? [obsRegisteredOutputs[id]]
          : ownedRegisteredOutputs) as Destination['outputs'][number][],
      };
    },
    staleTime: Infinity,
    refetchInterval: id === 'default' ? 1000 * 3 : undefined,
  });

export const outputQueryOptions = ({ outputId }: { outputId: OutputId }) => {
  if (outputId === 'default') {
    throw new Error('Use defaultOutputQueryOptions instead');
  }

  return queryOptions({
    queryKey: ['streamDestinations', 'output', outputId],
    queryFn: async () => {
      await ensureOBSPresence();
      return (await promisefy(window.host.getAllStreamingOutputs)())[outputId] ?? null;
    },
    placeholderData: keepPreviousData,
  });
};

export const defaultOutputQueryOptions = () => {
  return queryOptions({
    queryKey: ['streamDestinations', 'output', 'default'],
    refetchInterval: 1000 * 3,
    queryFn: async () => {
      await ensureOBSPresence();
      const defaultOutput = (await promisefy(window.host.getAllStreamingOutputs)())['default']!;
      return defaultOutput;
    },
    placeholderData: keepPreviousData,
  });
};
